import { forwardRef, useEffect, useMemo, useState } from 'react';

import { Field, HStack, IconButton, NumberInput, useRecipe, useSlotRecipe } from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { IconsLinear } from '@nocowanie/icons';

import { MobileNumberInputProps } from './mobile-number.input.props';
import { defaultMobileNumberInputTranslation } from './mobile-number.input.translation.model';

export const MobileNumberInput = forwardRef<HTMLInputElement, MobileNumberInputProps>(
    (props: MobileNumberInputProps, ref) => {
        const [focused, setFocused] = useState(false);
        const {
            onValueChange,
            disableIncrement,
            disableDecrement,
            placeholder,
            required,
            value,
            translationData = defaultMobileNumberInputTranslation,
            step = 1,
            min,
            max,
            inputClassName,
            autoFocus,
            'data-testid': testId,
        } = props;

        const [inputValue, setInputValue] = useState(value);

        useEffect(() => setInputValue(value), [value]);

        const isInvalid = useMemo(
            () => focused && (required ?? false) && isNil(value),
            [focused, required, value],
        );

        const recipe = useSlotRecipe({
            key: 'mobileNumberInput',
        });
        const styles = recipe();
        const inputRecipe = useRecipe({
            key: 'input',
            colorPalette: 'secondary',
        });
        const inputStyles = inputRecipe({
            size: 'md',
            variant: 'flushed',
        });

        return (
            <Field.Root invalid={isInvalid} ref={ref} w={'auto'}>
                <NumberInput.Root
                    unstyled
                    max={max}
                    min={min}
                    step={step}
                    required={required}
                    pattern={'^[0-9]+$'}
                    autoFocus={autoFocus}
                    value={inputValue}
                    onValueChange={onValueChange}
                    size={'lg'}
                >
                    <HStack maxW="240px" gap="2">
                        <NumberInput.DecrementTrigger asChild>
                            <IconButton
                                css={styles.button}
                                aria-label={translationData.buttonDecreaseLabel}
                                rounded="full"
                                size="sm"
                                variant="outline"
                                disabled={disableDecrement}
                                data-testid={'number-input-dec'}
                            >
                                <IconsLinear.Minus size={'sm'} />
                            </IconButton>
                        </NumberInput.DecrementTrigger>
                        <NumberInput.Input
                            css={inputStyles}
                            {...styles.input}
                            onBlurCapture={() => setFocused(true)}
                            placeholder={placeholder}
                            className={inputClassName}
                            onPaste={event => event.preventDefault()}
                            data-testid={testId ?? 'number-input'}
                        />
                        <NumberInput.IncrementTrigger asChild>
                            <IconButton
                                css={styles.button}
                                aria-label={translationData.buttonIncreaseLabel}
                                rounded="full"
                                size="sm"
                                variant="outline"
                                disabled={disableIncrement}
                                data-testid={'number-input-inc'}
                            >
                                <IconsLinear.Add size={'sm'} />
                            </IconButton>
                        </NumberInput.IncrementTrigger>
                    </HStack>
                </NumberInput.Root>
            </Field.Root>
        );
    },
);
